import * as React from "react"
import { toast } from "sonner"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { useUser } from "@/context/UserContext"
import { useBookingT } from "@/hooks/use-translations"
import { submitIntegrationRequest } from "@/features/integrations/api/requestForm"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Integration name to prefill in `appName` field. */
  initialAppName?: string
}

export function RequestIntegrationModal({ open, onOpenChange, initialAppName = "" }: Props) {
  const t = useBookingT()
  const { user } = useUser()

  const defaultCompany = user?.companyName || user?.firstName || ""
  const defaultAccount = user?.email ?? ""

  const [appName, setAppName] = React.useState(initialAppName)
  const [company, setCompany] = React.useState(defaultCompany)
  const [account, setAccount] = React.useState(defaultAccount)
  const [comment, setComment] = React.useState("")
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [errors, setErrors] = React.useState<Record<string, string[]>>({})

  React.useEffect(() => {
    if (open) {
      setAppName(initialAppName)
      setCompany(defaultCompany)
      setAccount(defaultAccount)
      setComment("")
      setErrors({})
    }
  }, [open, initialAppName, defaultCompany, defaultAccount])

  const fieldError = (field: string): string | undefined => {
    return errors[field]?.[0]
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setErrors({})
    try {
      const result = await submitIntegrationRequest({ appName, company, account, comment })
      if (result.success) {
        toast.success(t("integrations_request_success", "Request sent successfully"))
        onOpenChange(false)
      } else {
        setErrors(result.errors)
      }
    } catch (err) {
      toast.error(t("integrations_request_error", "Failed to send request"))
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("integrations_request_title", "Request integration")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ri-app">{t("integrations_request_app", "Application name")}</Label>
            <Input
              id="ri-app"
              value={appName}
              onChange={(e) => setAppName(e.target.value)}
              required
              className={fieldError("appName") ? "border-error-500 focus-visible:ring-error-500" : ""}
            />
            {fieldError("appName") && <p className="text-sm text-error-500">{fieldError("appName")}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="ri-company">{t("integrations_request_company", "Company")}</Label>
            <Input
              id="ri-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              required
              className={fieldError("company") ? "border-error-500 focus-visible:ring-error-500" : ""}
            />
            {fieldError("company") && <p className="text-sm text-error-500">{fieldError("company")}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="ri-account">{t("integrations_request_account", "Account")}</Label>
            <Input
              id="ri-account"
              value={account}
              onChange={(e) => setAccount(e.target.value)}
              required
              className={fieldError("account") ? "border-error-500 focus-visible:ring-error-500" : ""}
            />
            {fieldError("account") && <p className="text-sm text-error-500">{fieldError("account")}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="ri-comment">{t("integrations_request_comment", "Comment")}</Label>
            <Textarea
              id="ri-comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              {t("integrations_disconnect_cancel", "Cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {t("integrations_request_submit", "Send request")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
